/**
 * Vector plans — DXF, SVG and GeoJSON.
 *
 * A raster plan has to be traced, and tracing is an estimate: the outline comes
 * back within a pixel or two of the truth and the scale has to be calibrated by
 * hand. A vector drawing does not need any of that. The shapes are already
 * closed polygons with exact coordinates, and a DXF carries its own units, so
 * an imported CAD floor is as accurate as the drawing it came from.
 *
 * Two ways in, and the module decides which the file supports:
 *
 *   EXACT     the file contains closed shapes (filled SVG paths, closed DXF
 *             polylines, GeoJSON polygons). Those become regions directly.
 *   TRACED    the file is only linework — walls with no room boundaries, which
 *             is the common case for an architect's DXF. The linework is
 *             rasterised and handed to the same segmenter a PNG goes through,
 *             which floods the space BETWEEN the walls to find rooms.
 *
 * Layers do for a CAD file what fill colour does for a coloured plan: they say
 * what a shape is. So each layer gets a stable colour, which feeds the same
 * colour-group mapper, and a layer whose name says "corridor" or "void" is
 * proposed accordingly.
 */

import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader';
import { area as polyArea, centroid, bounds, ensureWinding } from './geometry';

/** Rasterise vector linework at this width when tracing is needed. */
export const VECTOR_RASTER_WIDTH = 1800;

// ── layer semantics ─────────────────────────────────────────────────────────

const LAYER_RULES = [
    [/(corridor|circulat|mall|concourse|lobby|walkway|promenade|passage)/i, 'circulation'],
    [/(void|atrium|open.?to.?below|opening)/i, 'void'],
    [/(lift|elevat|escalat|stair|core|wc|toilet|restroom|washroom)/i, 'amenity'],
    [/(plant|service|boh|back.?of.?house|store|riser|duct|electrical|mech)/i, 'service'],
    [/(landscap|planter|water|soft|garden)/i, 'landscape'],
    [/(site|external|car.?park|parking|road|apron)/i, 'exterior'],
    [/(retail|unit|shop|tenan|lease|gla)/i, 'tenancy'],
];

/** What a layer name says a shape is, or null if it says nothing. */
export function kindFromLayer(layer) {
    if (!layer) return null;
    for (let i = 0; i < LAYER_RULES.length; i++) {
        if (LAYER_RULES[i][0].test(layer)) return LAYER_RULES[i][1];
    }
    return null;
}

/**
 * A stable colour per layer.
 *
 * Layers are to a CAD file what category washes are to a coloured plan, so
 * giving each one a distinct colour lets the importer's colour-group mapper
 * work on a DXF exactly as it does on a PNG — say what a layer is once, and
 * every shape on it follows.
 */
export function colorForLayer(name) {
    let h = 2166136261 >>> 0;
    const s = String(name || 'default');
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    const hue = h % 360;
    const sat = 45 + ((h >>> 9) % 25);
    const light = 58 + ((h >>> 17) % 14);
    return hslToHex(hue, sat, light);
}

function hslToHex(h, s, l) {
    const S = s / 100;
    const L = l / 100;
    const k = (n) => (n + (h / 30)) % 12;
    const a = S * Math.min(L, 1 - L);
    const f = (n) => L - (a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1))));
    const to = (v) => Math.round(255 * v).toString(16).padStart(2, '0');
    return `#${to(f(0))}${to(f(8))}${to(f(4))}`;
}

// ── DXF ─────────────────────────────────────────────────────────────────────

/**
 * A deliberately small DXF reader: the entity types a floor plan is drawn with,
 * and nothing else.
 *
 * DXF is a flat stream of (group code, value) pairs, which is why a full parser
 * is unnecessary here — walking the pairs and switching on entity type covers
 * LINE, LWPOLYLINE, POLYLINE, CIRCLE and ARC, and those five carry essentially
 * every mall plan. Blocks and their inserts are NOT expanded; a plan that keeps
 * its tenancies inside blocks comes through as linework and gets traced, which
 * is the correct fallback rather than a silent hole in the model.
 */
export function parseDxf(text) {
    const lines = text.split(/\r\n|\r|\n/);
    const pairs = [];
    for (let i = 0; i + 1 < lines.length; i += 2) {
        const code = parseInt(lines[i].trim(), 10);
        if (Number.isNaN(code)) continue;
        pairs.push([code, lines[i + 1]]);
    }

    const shapes = [];
    let insideEntities = false;
    let i = 0;

    const readEntity = (type) => {
        const props = new Map();
        const vertices = [];
        let pendingX = null;
        i += 1;
        while (i < pairs.length && pairs[i][0] !== 0) {
            const [code, raw] = pairs[i];
            const value = raw.trim();
            if (code === 10) {
                pendingX = parseFloat(value);
            } else if (code === 20 && pendingX !== null) {
                vertices.push([pendingX, parseFloat(value)]);
                pendingX = null;
            } else {
                props.set(code, value);
            }
            i += 1;
        }
        return { type, props, vertices };
    };

    while (i < pairs.length) {
        const [code, raw] = pairs[i];
        const value = (raw || '').trim();

        if (code === 2 && value === 'ENTITIES') { insideEntities = true; i += 1; continue; }
        if (code === 0 && value === 'ENDSEC') { insideEntities = false; i += 1; continue; }
        if (!insideEntities || code !== 0) { i += 1; continue; }

        const type = value;
        if (type === 'LINE' || type === 'LWPOLYLINE' || type === 'POLYLINE'
            || type === 'CIRCLE' || type === 'ARC' || type === 'VERTEX') {
            const e = readEntity(type);
            const layer = e.props.get(8) || '0';
            const flags = parseInt(e.props.get(70) || '0', 10);

            if (type === 'LINE' && e.vertices.length >= 2) {
                shapes.push({ points: e.vertices.slice(0, 2), closed: false, layer, fill: null });
            } else if (type === 'LWPOLYLINE' && e.vertices.length >= 2) {
                shapes.push({ points: e.vertices, closed: !!(flags & 1), layer, fill: null });
            } else if (type === 'POLYLINE') {
                // Old-style polylines carry their points in following VERTEX
                // entities, so open a run and let the VERTEX branch fill it.
                shapes.push({ points: [], closed: !!(flags & 1), layer, fill: null, awaitingVertices: true });
            } else if (type === 'VERTEX') {
                const open = shapes[shapes.length - 1];
                if (open && open.awaitingVertices && e.vertices.length) open.points.push(e.vertices[0]);
            } else if (type === 'CIRCLE' && e.vertices.length) {
                const r = parseFloat(e.props.get(40) || '0');
                if (r > 0) shapes.push({ points: arcPoints(e.vertices[0], r, 0, 360), closed: true, layer, fill: null });
            } else if (type === 'ARC' && e.vertices.length) {
                const r = parseFloat(e.props.get(40) || '0');
                const a0 = parseFloat(e.props.get(50) || '0');
                const a1 = parseFloat(e.props.get(51) || '0');
                if (r > 0) shapes.push({ points: arcPoints(e.vertices[0], r, a0, a1), closed: false, layer, fill: null });
            }
            continue;
        }

        if (type === 'SEQEND') {
            const open = shapes[shapes.length - 1];
            if (open) delete open.awaitingVertices;
        }
        i += 1;
    }

    shapes.forEach((sh) => { delete sh.awaitingVertices; });
    // DXF is y-up; every other coordinate space here is y-down.
    const flipped = shapes
        .filter((sh) => sh.points.length >= 2)
        .map((sh) => ({ ...sh, points: sh.points.map(([x, y]) => [x, -y]) }));

    return { shapes: flipped, units: 'drawing' };
}

function arcPoints(centre, r, startDeg, endDeg) {
    const [cx, cy] = centre;
    let sweep = endDeg - startDeg;
    while (sweep <= 0) sweep += 360;
    const steps = Math.max(8, Math.ceil(sweep / 6));
    const pts = [];
    for (let i = 0; i <= steps; i++) {
        const a = ((startDeg + ((sweep * i) / steps)) * Math.PI) / 180;
        pts.push([cx + (Math.cos(a) * r), cy + (Math.sin(a) * r)]);
    }
    return pts;
}

// ── SVG ─────────────────────────────────────────────────────────────────────

/**
 * SVG via three's SVGLoader, which already flattens curves and arcs and applies
 * transforms. Writing another path parser to get that slightly wrong would be
 * a poor use of everyone's time.
 */
export function parseSvg(text) {
    const loader = new SVGLoader();
    const data = loader.parse(text);
    const shapes = [];

    data.paths.forEach((path, idx) => {
        const style = path.userData && path.userData.style ? path.userData.style : {};
        const fill = (style.fill && style.fill !== 'none') ? style.fill : null;
        const layer = (path.userData && path.userData.node && path.userData.node.getAttribute
            && (path.userData.node.getAttribute('data-layer')
                || path.userData.node.getAttribute('id')
                || path.userData.node.getAttribute('class'))) || `path-${idx}`;

        path.subPaths.forEach((sub) => {
            const pts = sub.getPoints(48).map((p) => [p.x, p.y]);
            if (pts.length < 2) return;
            shapes.push({ points: pts, closed: !!fill, layer, fill });
        });
    });

    return { shapes, units: 'px' };
}

// ── GeoJSON ─────────────────────────────────────────────────────────────────

export function parseGeoJson(text) {
    const doc = JSON.parse(text);
    const features = doc.type === 'FeatureCollection' ? doc.features : [doc];
    const shapes = [];

    const push = (ring, props) => {
        if (!Array.isArray(ring) || ring.length < 3) return;
        shapes.push({
            points: ring.map(([x, y]) => [x, -y]),   // GeoJSON is y-up
            closed: true,
            layer: (props && (props.layer || props.category || props.kind)) || 'features',
            fill: (props && props.fill) || null,
            props: props || {},
        });
    };

    features.forEach((f) => {
        const g = f.geometry || f;
        if (!g || !g.coordinates) return;
        if (g.type === 'Polygon') push(g.coordinates[0], f.properties);
        else if (g.type === 'MultiPolygon') g.coordinates.forEach((poly) => push(poly[0], f.properties));
        else if (g.type === 'LineString') {
            shapes.push({ points: g.coordinates.map(([x, y]) => [x, -y]), closed: false, layer: 'lines', fill: null });
        }
    });

    return { shapes, units: 'crs' };
}

// ── shared post-processing ──────────────────────────────────────────────────

/**
 * Fit every shape into a 0..1 box that preserves the drawing's aspect ratio,
 * and report the real-world extent so a DXF can skip calibration entirely.
 */
export function normaliseShapes(shapes) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    shapes.forEach((sh) => sh.points.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }));

    const w = maxX - minX;
    const h = maxY - minY;
    if (!(w > 0) || !(h > 0)) return null;

    // Normalise on the LONGER axis so nothing is squashed; the shorter axis
    // then occupies less than the full 0..1, exactly like a letterboxed image.
    const span = Math.max(w, h);
    const offX = (span - w) / 2;
    const offY = (span - h) / 2;

    return {
        shapes: shapes.map((sh) => ({
            ...sh,
            points: sh.points.map(([x, y]) => [
                (x - minX + offX) / span,
                (y - minY + offY) / span,
            ]),
        })),
        extent: { w, h, span },
    };
}

/**
 * Draw the linework into a canvas.
 *
 * Serves two purposes at once: it is the preview the operator sees behind the
 * review overlay, and it is the input to the segmenter when the drawing has no
 * closed shapes of its own. Fills are painted first, then every edge is stroked
 * dark, which is exactly the input the raster pipeline expects.
 */
export function rasteriseShapes(shapes, width = VECTOR_RASTER_WIDTH) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = width;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fcfcfd';
    ctx.fillRect(0, 0, width, width);

    const trace = (pts, closed) => {
        ctx.beginPath();
        pts.forEach(([x, y], i) => {
            const px = x * width;
            const py = y * width;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        });
        if (closed) ctx.closePath();
    };

    shapes.forEach((sh) => {
        if (!sh.closed || !sh.fill) return;
        trace(sh.points, true);
        ctx.fillStyle = sh.fill === true ? colorForLayer(sh.layer) : sh.fill;
        ctx.fill();
    });

    ctx.strokeStyle = '#141418';
    ctx.lineWidth = Math.max(1.5, width / 900);
    ctx.lineJoin = 'round';
    shapes.forEach((sh) => {
        trace(sh.points, sh.closed);
        ctx.stroke();
    });

    return canvas;
}

/**
 * Closed shapes straight to regions, skipping segmentation entirely.
 *
 * Only used when the drawing actually carries room boundaries. The polygons are
 * the drawing's own, so there is no tracing error to simplify away and no
 * anti-aliasing to fight.
 */
export function shapesToRegions(shapes, minAreaFraction = 0.00005) {
    const out = [];
    shapes.forEach((sh, idx) => {
        if (!sh.closed || sh.points.length < 3) return;
        const polygon = ensureWinding(sh.points, true);
        const a = polyArea(polygon);
        if (a < minAreaFraction) return;

        const color = sh.fill && sh.fill !== true ? sh.fill : colorForLayer(sh.layer);
        const kind = kindFromLayer(sh.layer) || 'tenancy';
        const b = bounds(polygon);

        out.push({
            id: `v${idx}`,
            polygon,
            centroid: centroid(polygon),
            areaFraction: a,
            pixelCount: Math.round(a * 1e6),
            color,
            colorKey: color,
            layer: sh.layer,
            coloured: !!sh.fill,
            solidity: 1,
            aspect: +(Math.max(b.w, b.h) / Math.max(1e-6, Math.min(b.w, b.h))).toFixed(2),
            enclosedShare: 1,
            vertices: polygon.length,
            kind,
            kindGuess: kind,
        });
    });

    out.sort((a, b) => b.areaFraction - a.areaFraction);
    return out;
}

/** Group by colour for the importer's mapper, mirroring segment.js. */
export function summariseVectorPalette(regions) {
    const byColor = new Map();
    regions.forEach((r) => {
        const e = byColor.get(r.colorKey)
            || { color: r.colorKey, count: 0, areaFraction: 0, coloured: r.coloured, layer: r.layer };
        e.count += 1;
        e.areaFraction += r.areaFraction;
        byColor.set(r.colorKey, e);
    });
    return [...byColor.values()].sort((a, b) => b.count - a.count);
}
